import { ThemeWrapper, useMobileLayout } from "@verity/components";
import { CoreCard, CoreTitleCard, coreToast } from "@verity/components/core";
import { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import BackButton from "../../../components/back-button";
import apiClient from "../../../helpers/api-client";
import { initiateAuthenticationRedirect } from "../../../helpers/oauth";
import { getProduct } from "../../../helpers/url";
import i18n from "../../../i18n";
import PracticeAssessment from "../../../images/portal/practice-assessment.svg?react";
import StandaloneAssessment from "../../../images/portal/standalone-assessment.svg?react";
import VerityAssessment from "../../../images/portal/verity-assessment.svg?react";
import { capitalizeFirstLetter } from "../../../reports/shared/helpers";
import { PortalControllerContext } from "../../context";
import { PORTAL_TOAST_CONTAINER_ID } from "../../shared";
import { createPracticeQuiz } from "../assessments-page/assessments-page-helper";
import CreateStandaloneAssessmentSection from "../assessments-page/CreateStandaloneAssessmentSection";
import CreateThirdPartyQuiz from "../assessments-page/createThirdPartyAssessment";
import VerityAssessmentSection from "../assessments-page/verity-assessment";
import {
	ConfigHeader,
	CreateAssessmentFooter,
	CreateAssessmentTile,
} from "../assessments-page/verity-assessment/shared";
import VisualizerBanner from "../visualizer-banner";
import styles from "./index.module.css";

type AssessmentType = "practice" | "standalone" | "verity" | "thirdParty";

export default function CreateAssessmentPage({
	setShowCreateAssessment,
	refreshPage,
}: {
	setShowCreateAssessment: (value: boolean) => void;
	refreshPage: () => Promise<void>;
}) {
	const { portalContext, action, setAction, verityInstituteSettings } = useContext(PortalControllerContext);
	const { lms, instituteUuid, courseUuid, courseId, lmsProvidedCourseId } = portalContext;
	const isMobileLayout = useMobileLayout();

	const verityAssessmentAllowed = useMemo(() => {
		if (!lms) return false;
		return verityInstituteSettings?.allowVerityAssessment;
	}, [lms, verityInstituteSettings?.allowVerityAssessment]);

	const [selectedAssessment, setSelectedAssessment] = useState<AssessmentType | null>(
		verityAssessmentAllowed ? "verity" : null,
	);
	const [creatingAssessment, setCreatingAssessment] = useState<boolean>(false);

	const [createThirdPartyDialogShowing, setCreateThirdPartyDialogShowing] = useState(false);

	// no LMS and verity assessment not allowed -> only standalone available
	const [createStandaloneDialogShowing, setCreateStandaloneDialogShowing] = useState(!lms && !verityAssessmentAllowed);
	const [createVerityAssessmentShowing, setCreateVerityAssessmentShowing] = useState(false);

	const isCandidateInstitute = useMemo(() => {
		const application = getProduct();

		if (application === "candidate") {
			return true;
		}
		return false;
	}, []);

	const isMainPage = useMemo(() => {
		return !createStandaloneDialogShowing && !createVerityAssessmentShowing;
	}, [createStandaloneDialogShowing, createVerityAssessmentShowing]);

	const tiles = useMemo(() => {
		const items: ReactNode[] = [];

		if (!isCandidateInstitute && verityAssessmentAllowed && lms) {
			items.push(
				<CreateAssessmentTile
					icon={<VerityAssessment />}
					title={i18n.t("portal.components.createAssessmentPageTSX.verityAssessment.title")}
					description={i18n.t("portal.components.createAssessmentPageTSX.verityAssessment.description", {
						lms: capitalizeFirstLetter(lms),
					})}
					onClick={() => setSelectedAssessment("verity")}
					selected={selectedAssessment === "verity"}
					key={"verityAssessment"}
				/>,
			);
		}

		// kept for dev testing (quick third party test of platform type of "Other")
		// if (lms) {
		// 	items.push(
		// 		<CreateAssessmentTile
		// 			icon={<PracticeAssessment />}
		// 			title={i18n.t("portal.components.createAssessmentPageTSX.thirdPartyAssessment.title")}
		// 			description={i18n.t("portal.components.createAssessmentPageTSX.thirdPartyAssessment.description")}
		// 			onClick={() => setSelectedAssessment("thirdParty")}
		// 			selected={selectedAssessment === "thirdParty"}
		// 		/>,
		// 	);
		// }
		if (instituteUuid && courseUuid) {
			const description = lms
				? i18n.t("portal.components.createAssessmentPageTSX.standaloneAssessment.description", {
						lms: capitalizeFirstLetter(lms),
					})
				: i18n.t("portal.components.createAssessmentPageTSX.standaloneAssessment.descriptionNoLms");
			items.push(
				<CreateAssessmentTile
					icon={<StandaloneAssessment />}
					title={i18n.t("portal.components.createAssessmentPageTSX.standaloneAssessment.title")}
					description={description}
					onClick={() => setSelectedAssessment("standalone")}
					selected={selectedAssessment === "standalone"}
					key="standalone"
				/>,
			);
		}

		if (lms === "canvas" || lms === "desire2learn") {
			items.push(
				<CreateAssessmentTile
					icon={<PracticeAssessment />}
					title={i18n.t("portal.components.createAssessmentPageTSX.practiceAssessment.title")}
					description={i18n.t("portal.components.createAssessmentPageTSX.practiceAssessment.description")}
					onClick={() => setSelectedAssessment("practice")}
					selected={selectedAssessment === "practice"}
					key="practice"
				/>,
			);
		}
		return items;
	}, [courseUuid, instituteUuid, isCandidateInstitute, lms, selectedAssessment, verityAssessmentAllowed]);

	const handleCreateQuiz = useCallback(async () => {
		coreToast.dismiss();
		const loadingToastId = coreToast.loading(i18n.t("portal.components.assessmentPage.practiceQuizCompTSX.createAss"), {
			containerId: PORTAL_TOAST_CONTAINER_ID,
		});
		try {
			// Check if lms is supported
			if (lms != "canvas" && lms != "desire2learn" && lms != "BlackboardLearn") {
				throw Error(i18n.t("portal.components.assessmentPage.practiceQuizCompTSX.noSupportLms")); // unsupported lms
			}
			// Get institute origin and api key id
			const instituteInfo = await apiClient.lmsInfo.getInstituteLmsInfo();
			if (!instituteInfo?.origin) {
				throw Error(i18n.t("portal.components.assessmentPage.practiceQuizCompTSX.lms"));
			} else if (!instituteInfo?.lmsApiClientId || instituteInfo.lmsApiClientId.trim() === "") {
				throw Error(i18n.t("portal.components.assessmentPage.practiceQuizCompTSX.noFeature"));
			} else if (courseId == null || !lmsProvidedCourseId) {
				throw Error("No course found.");
			}
			const oauthTokenExists = await apiClient.oauth.refreshStoredToken();
			if (oauthTokenExists || lms === "BlackboardLearn") {
				await createPracticeQuiz(lms, lmsProvidedCourseId, instituteInfo.origin);
				coreToast.update(
					loadingToastId,
					i18n.t("portal.components.assessmentPage.practiceQuizCompTSX.success"),
					"success",
					{
						containerId: PORTAL_TOAST_CONTAINER_ID,
						autoClose: 2500,
					},
				);

				void refreshPage();
				setShowCreateAssessment(false);
			}
			// Authorization Required
			else {
				localStorage.setItem(
					"initialPortalState",
					JSON.stringify({
						action: "createPracticeQuiz",
					}),
				);
				await initiateAuthenticationRedirect({
					courseId,
					callbackUrl: window.location.href,
				});
			}
		} catch (err) {
			coreToast.update(
				loadingToastId,
				`${i18n.t("portal.components.assessmentPage.practiceQuizCompTSX.quizCreateFail")}`,
				"error",
				{
					containerId: PORTAL_TOAST_CONTAINER_ID,
					autoClose: 2500,
				},
			);
			console.error(err);
		}
	}, [courseId, lms, lmsProvidedCourseId, refreshPage, setShowCreateAssessment]);

	const handleNext = useCallback(async () => {
		if (!selectedAssessment) return;

		try {
			setCreatingAssessment(true);
			if (selectedAssessment === "practice") {
				await handleCreateQuiz();
			} else if (selectedAssessment === "standalone") {
				setCreateStandaloneDialogShowing(true);
			} else if (selectedAssessment === "thirdParty") {
				setCreateThirdPartyDialogShowing(true);
			} else if (selectedAssessment === "verity") {
				if (lms === "canvas" || lms === "desire2learn") {
					await apiClient.oauth.refreshStoredToken();
				}
				setCreateVerityAssessmentShowing(true);
			}
		} catch (e) {
			console.error(e);
		} finally {
			setCreatingAssessment(false);
		}
	}, [handleCreateQuiz, selectedAssessment]);

	const cancelButton = useMemo(() => {
		return (
			<BackButton
				variant="cancel"
				onBack={() => {
					void refreshPage();
					setShowCreateAssessment(false);
				}}
				ariaLabel={i18n.t("portal.components.createAssessmentPageTSX.backButtonAriaLabel")}
			/>
		);
	}, [refreshPage, setShowCreateAssessment]);

	const mainContent = useMemo(() => {
		if (createStandaloneDialogShowing && instituteUuid && courseUuid) {
			return (
				<CreateStandaloneAssessmentSection
					instituteUuid={instituteUuid}
					courseUuid={courseUuid}
					onFinish={() => {
						void refreshPage();
						setShowCreateAssessment(false);
					}}
				/>
			);
		}

		if (createVerityAssessmentShowing) {
			return (
				<VerityAssessmentSection
					onFinish={() => {
						void refreshPage();
						setShowCreateAssessment(false);
					}}
				/>
			);
		}

		return (
			<CoreCard className={styles.mainCard} slotProps={{ bodyWrapper: { className: styles.mainContainer } }}>
				{!isMobileLayout && (
					<ConfigHeader variant="h1">
						{i18n.t("portal.components.createAssessmentPageTSX.createAssessment")}
					</ConfigHeader>
				)}
				<div className={styles.tiles}>{tiles}</div>
			</CoreCard>
		);
	}, [
		courseUuid,
		createStandaloneDialogShowing,
		createVerityAssessmentShowing,
		instituteUuid,
		isMobileLayout,
		refreshPage,
		setShowCreateAssessment,
		tiles,
	]);

	useEffect(() => {
		if (action === "createPracticeQuiz" && setAction) {
			void handleCreateQuiz().then(() => {
				setAction(undefined);
			});
		}
	}, []);

	return (
		<ThemeWrapper scopedCssBaselineProps={{ className: styles.page }}>
			<div className={styles.pageContainer}>
				<VisualizerBanner />
				{!isMobileLayout && <div>{cancelButton}</div>}
				{isMobileLayout && isMainPage && !createThirdPartyDialogShowing && (
					<CoreTitleCard
						title={i18n.t("portal.components.createAssessmentPageTSX.createAssessment")}
						topContent={cancelButton}
					/>
				)}
				{mainContent}
				{createThirdPartyDialogShowing && (
					<CreateThirdPartyQuiz
						onClose={() => setCreateThirdPartyDialogShowing(false)}
						onFinish={() => {
							void refreshPage();
							setCreateThirdPartyDialogShowing(false);
							setShowCreateAssessment(false);
						}}
					/>
				)}
			</div>
			{isMainPage && (
				<CreateAssessmentFooter
					slotProps={{
						back: { onClick: () => {}, disabled: true },
						next: {
							onClick: handleNext,
							disabled: selectedAssessment === null || creatingAssessment,
							loading: creatingAssessment,
						},
					}}
				/>
			)}
		</ThemeWrapper>
	);
}
